import clsx from "clsx";
// Hooks
import { RefObject, useMemo } from "react";
// Helpers
import { HEADER_CLASS_NAME } from "@/Washouts/constants";
import { formatWithParens, isAutoGeneratedFooter } from "@Layouts/components/Tables/DataSmartTable/helpers";
import { getFormattedDate } from "@dealerwave/dw-layer-date-converter";
import { useLocation } from "@Common/hooks/Location/useLocation.tsx";
// MUI
import { GridApi, GridCellParams, GridColDef, GridGroupingColDefOverride, GridRenderCellParams } from "@mui/x-data-grid-premium";
// Components
import { ExpandCollapseAllButtonHeader } from "@/Layouts/components/TreeGrid/ExpandCollapseAllButtonHeader";
import { TooltipTitleWrapper, TypographyCell } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";
import { TypographyCellProps } from "@/Layouts/components/Tables/DataSmartTable/types";
import { TypographyHeader } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyHeader";
// Types
import { prepareGroupColDef } from "@/Layouts/components/TreeGrid/prepareGroupColDef";
import { getFormatAmount } from "@/Common/helpers/number";

import { Box, Tooltip } from "@mui/material";
import { DATE_FORMAT } from "@/Constants/date";

interface IGenerateRenderingFnConfig {
	customFooterValue: string;
	parentInParens: boolean;
	customFormatFn?: (params: GridRenderCellParams) => string | null;
}

const generateRenderingFn = (
	otherTypographyCellProps?: Partial<Omit<TypographyCellProps, "params">>,
	config: Partial<IGenerateRenderingFnConfig> = {}
) => {
	return (param: GridRenderCellParams) => {
		let formattedValue = config.customFormatFn ? config.customFormatFn(param) : param.formattedValue;

		if (config.parentInParens && param.row?.isParent) {
			formattedValue = formatWithParens(param.value);
		}

		if (config.customFooterValue && isAutoGeneratedFooter(param.id)) {
			formattedValue = config.customFooterValue;
		}

		return <TypographyCell params={{ ...param, formattedValue }} {...otherTypographyCellProps} />;
	};
};

const baseColumnDefinition: Partial<GridColDef> = {
	align: "left",
	headerClassName: HEADER_CLASS_NAME,
	cellClassName: "table-noWrap special-units-cell",
	renderHeader: (params) => <TypographyHeader params={params} right={params.colDef.align === "right"} />,
	renderCell: generateRenderingFn({}, { parentInParens: true }),
	resizable: true,
	type: "string",
	width: 90,
	minWidth: 90,
	maxWidth: 200,
};

interface Row {
	isParent: boolean;
	search?: string;
}

interface Params {
	row: Row;
}

function searchRenderCell(params: Params) {
	if (params.row.isParent && params.row.search) {
		const onClickHandler = () => {
			navigator.clipboard.writeText(params.row.search ?? "");
		};
		return (
			<Tooltip
				arrow={true}
				title={
					<>
						<TooltipTitleWrapper text={params.row.search} />
						<Box textAlign="center">
							<TooltipTitleWrapper text="(click to copy)" />
						</Box>
					</>
				}
			>
				<Box component={"span"} onClick={onClickHandler}>
					{params.row?.search ?? ""}
				</Box>
			</Tooltip>
		);
	}
	return "";
}

export const useSpecialUnitsManagedDataTableConfig = (apiRef: RefObject<GridApi>, uniqueSTValues: string[]) => {
	const { timezone } = useLocation();

	const groupingColDef = useMemo<GridGroupingColDefOverride>(
		() => ({
			...prepareGroupColDef(),
			// headerName: "Group",
			renderHeader: () => <ExpandCollapseAllButtonHeader />,
			getApplyQuickFilterFn: () => null,
		}),
		[]
	);

	const renderCreatedByCell = (params: GridRenderCellParams) => {
		if (params.row.isParent) {
			return params.value?.fullName;
		}
		return "";
	};

	const renderStockIdCell = (params: GridRenderCellParams) => {
		if (params.row.isParent) {
			return `(${params.row?.descriptionCount})`;
		}
		return params.row.stockId;
	};

	const renderAgeCell = (params: GridRenderCellParams) => {
		if (params.row.isParent) {
			return `Avg. Days (${params.row.averageAge})`;
		}
		return params.row.age;
	};

	const renderDescriptionCell = (params: GridRenderCellParams) => {
		if (params.row.isParent) {
			return `(${params.row?.descriptionCount})`;
		}
		return (
			<Tooltip arrow={true} title={<TooltipTitleWrapper text={params.row?.description ?? ""} />}>
				<span>{params.row?.description ?? ""}</span>
			</Tooltip>
		);
	};

	const renderCriteriaCell = (params: GridRenderCellParams) => {
		if (params.row.isParent) {
			return params.row?.criteria;
		}
		return "";
	};

	const renderStatusCell = (params: GridRenderCellParams) => {
		if (params.row.isParent) {
			return `(${params.row?.statusCount})`;
		}
		return params.row?.status;
	};

	const renderCommissionCell = (params: GridRenderCellParams) => {
		if (params.row.isParent) {
			return getFormatAmount(params.row?.commission?.value, 2);
		}
		return "";
	};

	const columns: GridColDef[] = useMemo<GridColDef[]>(
		() => [
			{
				field: "_id",
				headerName: "ID",
				width: 10,
				filterable: false,
				getApplyQuickFilterFn: () => null,
			},
			{
				...baseColumnDefinition,
				field: "createdAt",
				headerName: "Entry Date",
				width: 140,
				valueFormatter: (value) => getFormattedDate(value, timezone, DATE_FORMAT.fullAbbrTime12hPadded),
				renderCell: (params) => <TypographyCell params={params} />,
			},
			{
				...baseColumnDefinition,
				field: "createdBy",
				headerName: "Added By",
				width: 130,
				renderCell: renderCreatedByCell,
			},
			{
				...baseColumnDefinition,
				field: "stockId",
				headerName: "Stock",
				type: "string",
				renderCell: renderStockIdCell,
			},
			{
				...baseColumnDefinition,
				field: "age",
				headerName: "Inv. Age",
				width: 100,
				renderCell: renderAgeCell,
			},
			{
				...baseColumnDefinition,
				field: "description",
				headerName: "Description",
				width: 140,
				maxWidth: 300,
				renderCell: renderDescriptionCell,
			},
			{
				...baseColumnDefinition,
				field: "search",
				headerName: "Search",
				type: "string",
				width: 130,
				renderCell: searchRenderCell,
			},
			{
				...baseColumnDefinition,
				field: "criteria",
				headerName: "Criteria",
				type: "string",
				renderCell: renderCriteriaCell,
			},
			{
				...baseColumnDefinition,
				field: "status",
				headerName: "Status",
				type: "string",
				renderCell: renderStatusCell,
			},
			{
				...baseColumnDefinition,
				field: "commission",
				headerName: "Commission",
				align: "right",
				renderCell: renderCommissionCell,
				cellClassName: (params: GridCellParams) =>
					clsx({
						"cell-with-dollar-symbol": params.row?.commission?.input === "Dollar",
						"cell-with-dollar-per-unit-symbol": params.row?.commission?.input === "Dollar per Unit",
						"cell-with-percent-symbol": params.row?.commission?.input === "Percent",
						"special-units-cell": true,
					}),
			},
		],
		[apiRef, uniqueSTValues]
	);
	return { columns, groupingColDef };
};
